'use client';

import React, { useEffect, useState } from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { AddIcon } from '@depop/web-ui-kit/Icons/AddIcon';
import { ArrowBackIcon } from '@depop/web-ui-kit/Icons/ArrowBackIcon';
import { ArrowRotateIcon } from '@depop/web-ui-kit/Icons/ArrowRotateIcon';
import { CancelIcon } from '@depop/web-ui-kit/Icons/CancelIcon';
import { ClickIcon } from '@depop/web-ui-kit/Icons/ClickIcon';
import { ClothesIcon } from '@depop/web-ui-kit/Icons/ClothesIcon';
import { CopyIcon } from '@depop/web-ui-kit/Icons/CopyIcon';
import { DepopGroupIconIcon } from '@depop/web-ui-kit/Icons/DepopGroupIconIcon';
import { FlashIcon } from '@depop/web-ui-kit/Icons/FlashIcon';
import { GlobeIcon } from '@depop/web-ui-kit/Icons/GlobeIcon';
import { IdentificationIcon } from '@depop/web-ui-kit/Icons/IdentificationIcon';
import { LocationIcon } from '@depop/web-ui-kit/Icons/LocationIcon';
import { ShopIcon } from '@depop/web-ui-kit/Icons/ShopIcon';
import { SparkleIcon } from '@depop/web-ui-kit/Icons/SparkleIcon';
import { TshirtAddIcon } from '@depop/web-ui-kit/Icons/TshirtAddIcon';
import { VideoBasicIcon } from '@depop/web-ui-kit/Icons/VideoBasicIcon';
import dynamic from 'next/dynamic';
import { useSelectedLayoutSegments } from 'next/navigation';

import { ATEvents } from './components/Tools/ATEvents';
import { ExperimentSwitcher } from './components/Tools/ExperimentSwitcher';
import { FigmaCapture } from './components/Tools/FigmaCapture';
import { GeoSwitch } from './components/Tools/GeoSwitch';
import { MagicLinkPaste } from './components/Tools/MagicLinkPaste';
import { PreviewEnv } from './components/Tools/PreviewEnv';
import { ProductInfo } from './components/Tools/ProductInfo';
import { RouteInfo } from './components/Tools/RouteInfo';
import { ShopInfo } from './components/Tools/ShopInfo';
import { SwitchUser } from './components/Tools/SwitchUser';
import { UserInfo } from './components/Tools/UserInfo';
import styles from './styles.module.css';

import { ToolCollection } from '@/components/AdminToolbox/components/ToolCollection';
import { ACTIVE_TOOL_SESSION_STORAGE_KEY } from '@/modules/adminTool/constants';
import { type Tools } from '@/modules/adminTool/types';
import { useDepopChromeExt } from '@/modules/adminTool/useDepopChromeExt';
import { useBoostedListingAccess } from '@/modules/boostedListings/hooks/useBoostedListingAccess';
import { useBulkListingEnabled } from '@/modules/bulkListing/useBulkListing';
import { isDevelopment } from '@/modules/env/isDevelopment';
import { isServerCheck } from '@/modules/env/isServerCheck';
import { isStaging } from '@/modules/env/isStaging';
import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from '@/modules/storage';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

const isNonProd = isDevelopment() || isStaging();

const BulkCreateUsers = isNonProd
  ? dynamic(
      () =>
        import('./components/Tools/BulkCreateUsers').then(
          (mod) => mod.BulkCreateUsers
        ),
      { ssr: false }
    )
  : null;
const BulkCreateProducts = isNonProd
  ? dynamic(
      () =>
        import('./components/Tools/BulkCreateProducts').then(
          (mod) => mod.BulkCreateProducts
        ),
      { ssr: false }
    )
  : null;

function VersionTag() {
  const githubUrl = `https://github.com/depop/web-default-website-server/commit/${process.env.NEXT_PUBLIC_APP_VERSION}`;

  if (process.env.NEXT_PUBLIC_DEPLOY_ENV === 'development') {
    return 'local';
  }

  return (
    <a
      className={styles.versionInformationLink}
      target="_blank"
      href={githubUrl}
    >
      {process.env.NEXT_PUBLIC_APP_VERSION}
    </a>
  );
}

export function AdminToolbox() {
  const lastActiveToolKey = !isServerCheck()
    ? getSessionStorageItem(ACTIVE_TOOL_SESSION_STORAGE_KEY)
    : '';
  const [open, setOpen] = useState(Boolean(lastActiveToolKey));
  const [activeToolKey, setActiveToolKey] = useState<string>(
    lastActiveToolKey || ''
  );
  const [, currentUser] = useCurrentUser();
  const { data: hasBulkListingAccess } = useBulkListingEnabled(currentUser?.id);
  const { hasBoostedListingsAccess } = useBoostedListingAccess();
  const [mounted, setMounted] = useState(false);
  const hasDepopChromeExt = useDepopChromeExt();
  const layoutSegments = useSelectedLayoutSegments();
  const isProductPage = layoutSegments[0] === 'products';
  const isShopPage = layoutSegments?.[1] === '(shop)';

  function isProdEnv() {
    return process.env.NEXT_PUBLIC_DEPLOY_ENV === 'production';
  }

  function toggleAdminToolOpen() {
    setOpen((value) => !value);
  }

  function openTool(toolKey: string) {
    setActiveToolKey(toolKey);
    setSessionStorageItem(ACTIVE_TOOL_SESSION_STORAGE_KEY, toolKey);
  }

  function closeActiveTool() {
    setActiveToolKey('');
    removeSessionStorageItem(ACTIVE_TOOL_SESSION_STORAGE_KEY);
  }

  const PRIMARY_TOOLS: Tools = {
    'AT events': {
      icon: <ClickIcon />,
      component: <ATEvents />,
    },
    Experiments: {
      icon: <FlashIcon />,
      component: <ExperimentSwitcher />,
    },
    Routing: {
      icon: <GlobeIcon />,
      component: <RouteInfo />,
    },
    ...(isNonProd && {
      'Geo switch': {
        icon: <LocationIcon />,
        component: <GeoSwitch />,
      },
    }),
    'Preview env': {
      icon: <VideoBasicIcon />,
      component: <PreviewEnv />,
    },
    'Switch user': {
      icon: <ArrowRotateIcon />,
      component: <SwitchUser />,
    },
    'Paste your magic link': {
      icon: <SparkleIcon />,
      component: <MagicLinkPaste />,
    },
    'Figma capture': {
      icon: <CopyIcon />,
      component: <FigmaCapture />,
    },
    ...(BulkCreateUsers && {
      'Bulk create users': {
        icon: <AddIcon />,
        component: <BulkCreateUsers />,
      },
    }),
    ...(BulkCreateProducts && {
      'Bulk create products': {
        icon: <TshirtAddIcon />,
        component: <BulkCreateProducts />,
      },
    }),
  };

  const USER_TOOLS = {
    'User info': {
      icon: <IdentificationIcon />,
      component: currentUser && (
        <UserInfo
          user={currentUser}
          hasBulkListingAccess={Boolean(hasBulkListingAccess)}
          hasBoostedListingsAccess={hasBoostedListingsAccess}
        />
      ),
    },
  };

  const PAGE_TOOLS: Tools = {
    ...(isProductPage && {
      Product: {
        icon: <ClothesIcon />,
        component: <ProductInfo />,
      },
    }),
    ...(isShopPage && {
      Shop: {
        icon: <ShopIcon />,
        component: <ShopInfo />,
      },
    }),
  };

  const TOOLS: Tools = {
    ...PRIMARY_TOOLS,
    ...USER_TOOLS,
    ...PAGE_TOOLS,
  };

  const activeTool = TOOLS[activeToolKey];
  const hasPageTools = Object.keys(PAGE_TOOLS).length > 0;

  useEffect(() => {
    if (!open) {
      closeActiveTool();
    }
  }, [open]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (mounted && (hasDepopChromeExt || !isProdEnv())) {
    return (
      <>
        <button
          className={styles.toggleButton}
          onClick={toggleAdminToolOpen}
          aria-label={open ? 'close admin tool' : 'open admin tool'}
        >
          {open ? <CancelIcon size={24} /> : <DepopGroupIconIcon size={32} />}
        </button>
        {open && (
          <div className={styles.modal}>
            {activeTool ? (
              <div>
                <div className={styles.actionIconWrapper}>
                  <button
                    className={styles.backButton}
                    onClick={closeActiveTool}
                  >
                    <ArrowBackIcon size={20} />
                  </button>
                </div>
                {activeTool.component}
              </div>
            ) : (
              <>
                {hasPageTools && (
                  <ToolCollection
                    collection={PAGE_TOOLS}
                    title="On this page"
                    onToolClick={openTool}
                  />
                )}
                {currentUser && (
                  <ToolCollection
                    collection={USER_TOOLS}
                    title="Authenticated user"
                    onToolClick={openTool}
                  />
                )}
                <ToolCollection
                  collection={PRIMARY_TOOLS}
                  title="Tools"
                  onToolClick={openTool}
                />
                <Caption
                  variant="lg-regular"
                  className={styles.versionInformation}
                >
                  Version: <VersionTag /> | Env:{' '}
                  {process.env.NEXT_PUBLIC_DEPLOY_ENV}
                </Caption>
              </>
            )}
          </div>
        )}
      </>
    );
  }

  return null;
}
